import { useState } from 'react';
import { Grid, Typography, IconButton, useTheme } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import FilteredTable from '../../TrackerTable/FilteredTable';
import { Spinner } from 'react-bootstrap';
import ErrorComponent from './ErrorComponent';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import EnlargedChartModal from '../EnlargedChartModal';
import { useLocation } from 'react-router-dom';
import { getTabColor } from '../../../shared/constants/constants';
import { useQuery } from '@tanstack/react-query';

const useStyles = makeStyles(({ tabColor }) => ({
  tableContainer: {
    maxHeight: '90%',
    overflow: 'scroll',
    border: 'solid black',
    borderWidth: '1px',
    borderRadius: '1px',
  },
  text: {
    paddingLeft: '5%',
    paddingTop: '1vh',
    color: '#ffffff',
    fontSize: '100%',
  },
  tableHeader: {
    backgroundColor: ({ tabColor }) => tabColor,
    background: '#002145',
    height: '5vh',
    textAlign: 'left',
  },
  fullScreenButton: {
    position: 'absolute',
    top: '3%',
    right: '1%',
    zIndex: '1000',
    color: 'white',
  },
  gridItem: {
    maxHeight: '50vh',
    position: 'relative',
    marginTop: '2.5vh',
  },
}));

function TableComponent({ tableData, query }) {
  //component for rendering the tables at bottom of analytics page
  const { pathname } = useLocation();

  const theme = useTheme();
  const classes = useStyles({ tabColor: getTabColor(pathname, theme) });
  const { columns, dropdownTable, networkRequest, queryKey } = tableData;

  const [open, setOpen] = useState(false);

  const buttonHandler = () => {
    setOpen(true);
  };
  const resolvedQueryKey = queryKey ?? tableData.title;

  const tableQuery = useQuery({
    queryKey: ['analytics-table', resolvedQueryKey, query],
    queryFn: async () => {
      const result = await networkRequest(query);
      return result?.data ? result.data : result;
    },
    enabled: Boolean(query),
    staleTime: 2 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: 1,
  });

  const issuesState = tableQuery.isLoading ? 'loading' : tableQuery.data ?? 'loading';

  const Container = ({ children }) => {
    return (
      <Grid item className={classes.gridItem} sm={6} lg={4}>
        <EnlargedChartModal open={open} onClose={() => setOpen(false)} display={children}></EnlargedChartModal>
        <IconButton className={classes.fullScreenButton} onClick={buttonHandler} size="large">
          <FullscreenIcon></FullscreenIcon>
        </IconButton>
        {children}
      </Grid>
    );
  };
  if (tableQuery.isError) {
    return <ErrorComponent message={tableQuery.error?.message ?? 'Failed to load table data.'}></ErrorComponent>;
  } else if (issuesState.error === 1) {
    return <ErrorComponent message={issuesState.message}></ErrorComponent>;
  } else if (issuesState !== 'loading' && dropdownTable !== 'loading') {
    return (
      <Container>
        <div className={classes.tableHeader}>
          <Typography className={classes.text} nowrap="true">
            {tableData.title}
          </Typography>
        </div>
        <div className={classes.tableContainer}>
          <FilteredTable type={tableData.type} issues={issuesState} cols={columns} dropdownTable={dropdownTable} />
        </div>
      </Container>
    );
  } else {
    return (
      <Container>
        <div className={classes.tableHeader}>
          <Typography className={classes.text} nowrap="true">
            {tableData.title}
          </Typography>
        </div>
        <div>
          <Typography>Waiting for data</Typography>
          <Spinner animation="border" />
        </div>
      </Container>
    );
  }
}

export default TableComponent;
